import { DataTable } from '@app/components/data-table/data-table';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import { toPickerPeriods } from '@app/components/multi-period-picker';
import {
  FilterOptions,
  useServerFilterOptions,
} from '@app/components/data-table/hooks/use-server-filter-options';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import { DataTableRowAction } from '@app/types/data-table';
import {
  FoodOperationActivityRecord,
  PaginatedFoodOperationActivityRecordList,
} from '@shared/api/types';
import { parseAsInteger, useQueryState } from 'nuqs';
import React, { useMemo } from 'react';
import SoldFoodAndDrinkItemSalesEditForm from './sold-food-and-drink-item-sales-edit-form';
import { useSoldFoodAndDrinkItemSalesTableColumns } from './sold-food-and-drink-item-sales-table-columns';

interface SoldFoodAndDrinkItemSalesTableProps {
  data: PaginatedFoodOperationActivityRecordList;
  status: 'error' | 'success' | 'loading';
  isLoading: boolean;
  isFetching?: boolean;
  isPreviousData: boolean;
  isFetchedAfterMount: boolean;
}

export default function SoldFoodAndDrinkItemSalesTable({
  data,
  status,
  isLoading,
  isPreviousData,
  isFetchedAfterMount,
}: SoldFoodAndDrinkItemSalesTableProps) {
  const [rowAction, setRowAction] =
    React.useState<DataTableRowAction<FoodOperationActivityRecord> | null>(
      null
    );
  const { showSimpleDialog, SimpleDialogComponent } = useSimpleDialog();
  const [perPage] = useQueryState('perPage', parseAsInteger.withDefault(10));

  const { count } = data || {};
  const pageCount = count ? Math.ceil(count / (perPage || 10)) : 0;

  const filterOptions = ['timePeriods', 'sites'] as const;

  const { timePeriodsFilter, sitesFilter } = useServerFilterOptions(
    filterOptions,
    data?.filters || {}
  );

  const useFilterOptions: FilterOptions<typeof filterOptions> = useMemo(
    () => ({
      timePeriods: timePeriodsFilter,
      sites: sitesFilter,
    }),
    [timePeriodsFilter, sitesFilter]
  );

  const { table } = useServerDataTable({
    data: data?.results || [],
    columns: useSoldFoodAndDrinkItemSalesTableColumns({
      filters: useFilterOptions,
      timePeriods: toPickerPeriods(data?.filters?.timePeriods),
    }),
    shallow: false,
    pageCount,
    initialState: {
      columnVisibility: {
        uuid: false,
      },
      columnOrder: [
        'uuid',
        'period',
        'site',
        'customer',
        'quantity',
        'totalMassOrVolume',
        'unit',
      ],
    },
  });

  return (
    <>
      <DataTable
        table={table}
        isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
        tableLoading={!data && status === 'loading'}
        className="bg-gray-00 text-left"
        rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
        onRowClick={(row) => {
          row && setRowAction({ variant: 'update', row });
          showSimpleDialog();
        }}
      >
        <DataTableToolbar table={table} />
      </DataTable>
      {SimpleDialogComponent({
        header: 'Edit sale',
        contentClassName: 'w-full max-w-3xl',
        open: rowAction?.variant === 'update',
        setOpen: () => setRowAction(null),
        content: (
          <SoldFoodAndDrinkItemSalesEditForm
            onOpenChange={() => setRowAction(null)}
            foodActivityRecord={
              rowAction?.row.original as FoodOperationActivityRecord
            }
            numberOfSales={data?.count || 0}
          />
        ),
      })}
    </>
  );
}
